// Display helpers for the org licensing dashboards. All API timestamps are UTC ISO strings.

export const toDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  // Firestore "unset" timestamps can come back as 0001-01-01; treat them as empty.
  return Number.isNaN(date.getTime()) || date.getFullYear() < 1971 ? null : date;
};

export const formatDateTime = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString() : '—';
};

export const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : '—';
};

export const formatRelative = (value) => {
  const date = toDate(value);
  if (!date) {
    return '—';
  }

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);
  let text;
  if (abs < 45) {
    return diffSeconds <= 0 ? 'just now' : 'in a moment';
  } else if (abs < 3600) {
    text = `${Math.round(abs / 60)} min`;
  } else if (abs < 86400) {
    const hours = Math.round(abs / 360) / 10;
    text = `${hours} h`;
  } else {
    const days = Math.round(abs / 86400);
    text = `${days} day${days === 1 ? '' : 's'}`;
  }

  return diffSeconds < 0 ? `${text} ago` : `in ${text}`;
};

export const daysUntil = (value) => {
  const date = toDate(value);
  return date ? Math.floor((date.getTime() - Date.now()) / 86400000) : null;
};

/** Days since the given time, or null when there is no time. */
export const daysSince = (value) => {
  const date = toDate(value);
  return date ? (Date.now() - date.getTime()) / 86400000 : null;
};

/** yyyy-mm-dd in local time, for <input type="date">. */
export const toDateInput = (value) => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) {
    return '';
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** The end of the chosen local day as a UTC ISO string, so a licence is valid through its expiry date. */
export const expiryIsoFromDateInput = (dateInput) =>
  dateInput ? new Date(`${dateInput}T23:59:59`).toISOString() : null;

/** Lower-cases a domain and strips a leading "@" or "*.". Returns '' when it doesn't look like a domain. */
export const normalizeDomain = (value) => {
  const domain = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^@/, '')
    .replace(/^\*\./, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? domain : '';
};

/** Splits "acme.com, @acme.co.uk acme.io" into a de-duplicated list of valid domains. */
export const parseDomainList = (text) => {
  const domains = String(text || '')
    .split(/[\s,;]+/)
    .map(normalizeDomain)
    .filter(Boolean);
  return Array.from(new Set(domains));
};

export const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const ACTION_LABELS = {
  Activated: 'Activated',
  Moved: 'Moved to another PC',
  Released: 'Released',
  ReleasedByIT: 'Revoked by IT',
  RoleChanged: 'Role changed',
  MemberAdded: 'Member added',
  MemberRemoved: 'Member removed',
  MemberBlocked: 'Member blocked',
  DomainsChanged: 'Domains changed',
  LicenseChanged: 'License changed',
  Created: 'Created',
  Downloaded: 'Installer downloaded',
};

/** File size for display, e.g. 182 MB. */
export const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/** Release notes as display lines: one per line, list markers removed. */
export const noteLines = (notes) =>
  String(notes || '')
    .split('\n')
    .map((line) => line.trim().replace(/^[-*•·]+\s*/, ''))
    .filter(Boolean);

export const formatAction = (action) => ACTION_LABELS[action] || action || '—';

/** Event details can be a string or an object; show something readable either way. */
export const formatDetails = (details) => {
  if (details === null || details === undefined || details === '') {
    return '';
  }
  if (typeof details === 'string') {
    return details;
  }
  try {
    return JSON.stringify(details);
  } catch (error) {
    return String(details);
  }
};
